export type ThemePreset =
  | 'dark-purple'
  | 'dark-gold'
  | 'light-gold'
  | 'light-blue'

export type ThemeOption = {
  id: ThemePreset
  label: string
  swatch: string
}

export const THEME_OPTIONS: ThemeOption[] = [
  {
    id: 'dark-purple',
    label: '深紫',
    swatch: 'linear-gradient(135deg, #8b5cff, #5b7bff)',
  },
  {
    id: 'dark-gold',
    label: '深金',
    swatch: 'linear-gradient(135deg, #ffd76b, #f0a824)',
  },
  {
    id: 'light-gold',
    label: '白金',
    swatch: 'linear-gradient(135deg, #fff8e8, #d4a72c)',
  },
  {
    id: 'light-blue',
    label: '白蓝',
    swatch: 'linear-gradient(135deg, #eef4ff, #2f62f0)',
  },
]

const PRESET_SET = new Set<string>(THEME_OPTIONS.map(o => o.id))

export function isThemePreset(v: unknown): v is ThemePreset {
  return typeof v === 'string' && PRESET_SET.has(v)
}

export function themeClass(preset: ThemePreset) {
  return `theme-${preset}`
}

export function applyThemeClass(preset: ThemePreset) {
  if (!import.meta.client) return
  const root = document.documentElement
  for (const opt of THEME_OPTIONS) {
    root.classList.remove(themeClass(opt.id))
  }
  root.classList.add(themeClass(preset))
}

export const useThemeStore = defineStore(
  'theme',
  () => {
    const preset = ref<ThemePreset>('dark-purple')

    const setTheme = (next: ThemePreset) => {
      if (!isThemePreset(next)) next = 'dark-purple'
      preset.value = next
      applyThemeClass(next)
    }

    /** 客户端启动：URL ?theme= 优先，否则用已 hydrate 的 preset 同步 DOM */
    const init = () => {
      const q = (location.search.match(/[?&]theme=([a-z-]+)/) || [])[1]
      if (isThemePreset(q)) {
        setTheme(q)
      } else {
        applyThemeClass(preset.value)
      }
    }

    return {
      preset,
      options: THEME_OPTIONS,
      setTheme,
      init,
    }
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
      pick: ['preset'],
      // theme-boot 首屏脚本直接读明文 preset
      serializer: {
        serialize: data => (data as { preset: ThemePreset }).preset,
        deserialize: value => ({
          preset: isThemePreset(value) ? value : 'dark-purple',
        }),
      },
    },
  },
)
